import React from "react";
import PropTypes from "prop-types";
import { ChipMedallion } from "./ChipMedallion.js";
import { Button } from "../ui/Button";

const SwitchOption = ({ opt, isSelected, onToggle }) => {
  const btnClass = `apple-switch-btn ${isSelected ? "active" : ""}`;
  const fontWeightStyle = isSelected ? "600" : "400";
  const trackBg = isSelected ? "var(--color-turquoise)" : "var(--color-border)";
  const thumbLeft = isSelected ? "18px" : "2px";

  return (
    <Button
      type="button"
      onClick={() => onToggle?.(opt)}
      className={btnClass}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
        padding: "4px 8px",
        border: "none",
        background: "transparent",
        cursor: "pointer",
        userSelect: "none",
        transition: "all 0.2s ease",
        color: "var(--color-text-primary)",
        fontWeight: fontWeightStyle,
        fontSize: "0.9rem",
      }}
      role="switch"
      aria-checked={isSelected}
    >
      <span>{opt}</span>
      <div
        style={{
          width: "36px",
          height: "20px",
          borderRadius: "10px",
          background: trackBg,
          position: "relative",
          transition: "background 0.25s ease",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            background: "#ffffff",
            position: "absolute",
            top: "2px",
            left: thumbLeft,
            transition: "left 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
          }}
        />
      </div>
    </Button>
  );
};

SwitchOption.propTypes = {
  opt: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onToggle: PropTypes.func,
};

const ChipOption = ({ opt, isSelected, onToggle }) => {
  const btnClass = `additive-chip ${isSelected ? "active" : ""}`;
  const borderStyle = isSelected
    ? "1.5px solid var(--color-turquoise)"
    : "1px solid var(--color-border)";
  const bgStyle = isSelected
    ? "rgba(64, 224, 208, 0.12)"
    : "var(--color-surface)";
  const colorStyle = isSelected
    ? "var(--color-text-primary)"
    : "var(--color-text-secondary)";
  const fontWeightStyle = isSelected ? "600" : "400";

  return (
    <Button
      type="button"
      onClick={() => onToggle?.(opt)}
      className={btnClass}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        padding: "6px 14px",
        borderRadius: "20px",
        border: borderStyle,
        background: bgStyle,
        color: colorStyle,
        fontWeight: fontWeightStyle,
        cursor: "pointer",
        userSelect: "none",
        transition: "all 0.2s ease",
      }}
      aria-pressed={isSelected}
    >
      <ChipMedallion label={opt} />
      <span style={{ fontSize: "0.9rem" }}>{opt}</span>
    </Button>
  );
};

ChipOption.propTypes = {
  opt: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onToggle: PropTypes.func,
};

const ToggleChipOption = ({ opt, isSelected, onToggle, variant }) => {
  if (variant === "switch") {
    return (
      <SwitchOption opt={opt} isSelected={isSelected} onToggle={onToggle} />
    );
  }
  return <ChipOption opt={opt} isSelected={isSelected} onToggle={onToggle} />;
};

ToggleChipOption.propTypes = {
  opt: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onToggle: PropTypes.func,
  variant: PropTypes.string,
};

export const ToggleChips = ({
  label,
  options = [],
  selectedItems = [],
  onToggle,
  variant = "chip",
}) => {
  return (
    <div className="toggle-group-container" style={{ marginBottom: "1.25rem" }}>
      {label && (
        <label
          style={{
            display: "block",
            marginBottom: "0.75rem",
            fontWeight: "600",
            fontSize: "0.95rem",
            color: "var(--color-text-primary)",
          }}
        >
          {label}
        </label>
      )}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          marginBottom: "0.5rem",
        }}
      >
        {(Array.isArray(options) ? options : []).map((opt) => {
          const isSelected = (
            Array.isArray(selectedItems) ? selectedItems : []
          ).includes(opt);

          return (
            <ToggleChipOption
              key={`toggle-${label}-${opt}`}
              opt={opt}
              label={label}
              isSelected={isSelected}
              onToggle={onToggle}
              variant={variant}
            />
          );
        })}
      </div>
    </div>
  );
};

ToggleChips.propTypes = {
  label: PropTypes.node,
  options: PropTypes.array,
  selectedItems: PropTypes.array,
  onToggle: PropTypes.func,
  variant: PropTypes.string,
};

export default ToggleChips;
